import { z } from 'zod';

export const Finish = z.enum(['nonfoil', 'foil', 'etched']);
export type Finish = z.infer<typeof Finish>;

export const Condition = z.enum(['NM', 'LP', 'MP', 'HP', 'DMG']);
export type Condition = z.infer<typeof Condition>;

/** A drawer, a deck, or the intake pile new cards land in. */
export const LocationKind = z.enum(['drawer', 'deck', 'intake']);
export type LocationKind = z.infer<typeof LocationKind>;

/** Scryfall language codes (en, ja, de, …, plus phyrexian "ph"). */
export const Lang = z.string().regex(/^[a-z]{2,3}$/, 'Language must be a Scryfall code such as en or ja');

const Color = z.enum(['W', 'U', 'B', 'R', 'G']);

/** One face of a card. Single-faced cards have one; split, flip, adventure and double-faced cards have two or more. */
export const CardFace = z.object({
  name: z.string(),
  manaCost: z.string().nullable(),
  typeLine: z.string(),
  oracleText: z.string(),
  power: z.string().nullable(),
  toughness: z.string().nullable(),
  loyalty: z.string().nullable(),
  defense: z.string().nullable(),
  colors: z.array(Color),
  flavorText: z.string().nullable(),
  artist: z.string().nullable(),
  /** Set only for double-faced cards, whose faces have their own images. */
  imageNormal: z.string().nullable(),
});
export type CardFace = z.infer<typeof CardFace>;

/** One printing, cached from Scryfall. Characteristics are the front face's unless noted. */
export const Card = z.object({
  scryfallId: z.string(),
  oracleId: z.string().nullable(),
  name: z.string(),
  setCode: z.string(),
  setName: z.string(),
  collectorNumber: z.string(),
  lang: z.string(),
  rarity: z.string(),
  /** The front face's colors for double-faced cards. */
  colors: z.array(Color),
  colorIdentity: z.array(Color),
  /** The whole type line, all faces ("Creature — Human Wizard // Creature — Human Insect"). */
  typeLine: z.string(),
  /** From the front face's type line: "Legendary Artifact Creature — Equipment Golem". */
  supertypes: z.array(z.string()),
  types: z.array(z.string()),
  subtypes: z.array(z.string()),
  manaCost: z.string().nullable(),
  cmc: z.number(),
  /** Rules text, all faces, separated by a line with "//". */
  oracleText: z.string(),
  power: z.string().nullable(),
  toughness: z.string().nullable(),
  loyalty: z.string().nullable(),
  defense: z.string().nullable(),
  keywords: z.array(z.string()),
  /** Colors of mana it can produce (W, U, B, R, G, C). */
  producedMana: z.array(z.string()),
  layout: z.string(),
  faces: z.array(CardFace),
  artist: z.string().nullable(),
  releasedAt: z.string().nullable(),
  /** On Wizards' Reserved List. */
  reserved: z.boolean(),
  /** Popularity rank on EDHREC (1 = most played), when known. */
  edhrecRank: z.number().nullable(),
  finishes: z.array(Finish),
  /** Scryfall's promo types for the printing, e.g. "surgefoil" when its foil is a surge foil. */
  promoTypes: z.array(z.string()),
  imageSmall: z.string().nullable(),
  imageNormal: z.string().nullable(),
  imageLarge: z.string().nullable(),
  /** Back face (normal size), for double-faced cards. */
  imageBack: z.string().nullable(),
  /** TCGplayer USD from Scryfall, as decimal strings (decision A3). */
  prices: z.object({ usd: z.string().nullable(), usdFoil: z.string().nullable(), usdEtched: z.string().nullable() }),
  /** Format → legal, not_legal, banned or restricted. */
  legalities: z.record(z.string(), z.string()),
  scryfallUri: z.string(),
  fetchedAt: z.string(),
});
export type Card = z.infer<typeof Card>;

/** A group of identical physical copies in one place. */
export const Copy = z.object({
  id: z.string(),
  card: Card,
  finish: Finish,
  lang: z.string(),
  condition: Condition,
  quantity: z.number().int().positive(),
  locationId: z.string(),
  updatedAt: z.string(),
});
export type Copy = z.infer<typeof Copy>;

export const Location = z.object({
  id: z.string(),
  /** Where it is, e.g. "Catalog 1 / Drawer 1", or a deck's name. */
  name: z.string(),
  /** What it holds, e.g. "White". Empty for decks. */
  label: z.string(),
  kind: LocationKind,
  sortOrder: z.number(),
  cardCount: z.number(),
  /** USD, from Scryfall's TCGplayer prices. Cards with no price count as 0. */
  value: z.number(),
  /** For a deck: where it sits on the /decks gallery (and the home page's deck list). */
  gallery: z.object({ section: z.string(), order: z.number().nullable(), label: z.string() }).nullable(),
});
export type Location = z.infer<typeof Location>;

export const Deck = z.object({
  format: z.string(),
  /** Scryfall ids of the commander printings. */
  commanders: z.array(z.string()),
  notes: z.string(),
  /** Set when the deck has a public read-only page at /d/<shareSlug> (decision A4). */
  shareSlug: z.string().nullable(),
  /** Its section on the /decks gallery ("" for none), its place in that section, and a label shown under it. */
  gallerySection: z.string(),
  galleryOrder: z.number().nullable(),
  galleryLabel: z.string(),
});
export type Deck = z.infer<typeof Deck>;

export const LocationList = z.object({ locations: z.array(Location) });
export type LocationList = z.infer<typeof LocationList>;

export const LocationDetail = z.object({ location: Location, deck: Deck.nullable(), copies: z.array(Copy) });
export type LocationDetail = z.infer<typeof LocationDetail>;

export const CopyResponse = z.object({ copy: Copy });

export const CopyList = z.object({ copies: z.array(Copy) });

/** Search across the collection. Every filter is optional; together they narrow (AND). */
export const CopySearch = z.object({
  /** Part of the card's name (either face of a double-faced card). */
  name: z.string().trim().max(100).optional(),
  /** Set code, e.g. "mh3". */
  set: z.string().trim().max(10).optional(),
  /** A drawer color group: White, Blue, Black, Red, Green, Multicolor, Colorless or Lands. */
  color: z.string().optional(),
  /** A type group: Creatures, Lands, Planeswalkers, Battles, Instants, Sorceries, Artifacts, Enchantments or Other. */
  type: z.string().optional(),
  finish: Finish.optional(),
  /** A foil type id: "foil" (plain), "etched", or a special foil's promo type such as "surgefoil". */
  foilType: z.string().max(40).optional(),
  locationId: z.string().optional(),
});
export type CopySearch = z.infer<typeof CopySearch>;

export const CopySearchResponse = z.object({
  copies: z.array(Copy.extend({ locationName: z.string() })),
  /** Cards (not groups) matching, including any past the cap. */
  total: z.number(),
  /** More groups matched than were returned. */
  truncated: z.boolean(),
});
export type CopySearchResponse = z.infer<typeof CopySearchResponse>;

/** Every copy of a card you own (any printing with the same oracle id), and where each one is. */
export const CardDetail = z.object({
  card: Card,
  copies: z.array(Copy.extend({ locationName: z.string() })),
});
export type CardDetail = z.infer<typeof CardDetail>;

/** The printing by Scryfall id, or by set code and collector number (as Moxfield exports list them). */
export const AddCopyRequest = z
  .object({
    scryfallId: z.uuid().optional(),
    set: z.string().trim().min(2).max(8).optional(),
    collectorNumber: z.string().trim().min(1).max(12).optional(),
    finish: Finish.default('nonfoil'),
    /** Defaults to the printing's own language. */
    lang: Lang.optional(),
    condition: Condition.default('NM'),
    quantity: z.number().int().min(1).max(999).default(1),
    /** Defaults to the intake location ("Incoming Unsorted"). */
    locationId: z.string().optional(),
  })
  .refine((r) => r.scryfallId || (r.set && r.collectorNumber), 'Give scryfallId, or set and collectorNumber.');
export type AddCopyRequest = z.input<typeof AddCopyRequest>;

/**
 * Changes some copies in a group. With `count` smaller than the group's
 * quantity, only that many copies change (they split off); otherwise the whole
 * group does. Lands on a matching group if one exists there already.
 */
export const UpdateCopyRequest = z
  .object({
    count: z.number().int().min(1).max(999).optional(),
    locationId: z.string(),
    finish: Finish,
    lang: Lang,
    condition: Condition,
  })
  .partial();
export type UpdateCopyRequest = z.infer<typeof UpdateCopyRequest>;

/**
 * Many copies at once, for seeding from Moxfield: by Scryfall id, into a given
 * location. Card data comes from Scryfall in batches of 75. All or nothing:
 * any unknown card, finish or location fails the whole batch before writing.
 */
export const AddCopiesBatchRequest = z.object({
  items: z
    .array(
      z.object({
        scryfallId: z.uuid(),
        finish: Finish.default('nonfoil'),
        lang: Lang.optional(),
        condition: Condition.default('NM'),
        quantity: z.number().int().min(1).max(999).default(1),
        locationId: z.string(),
      }),
    )
    .min(1)
    .max(500),
});
export type AddCopiesBatchRequest = z.input<typeof AddCopiesBatchRequest>;

export const AddCopiesBatchResponse = z.object({ added: z.number(), groups: z.number() });

/** Removes `count` copies from a group (default: all of them). */
export const RemoveCopyRequest = z.object({ count: z.number().int().min(1).max(999).optional() });

export const CreateDeckRequest = z.object({
  name: z.string().trim().min(1).max(100),
  format: z.string().trim().max(30).default('commander'),
});

export const UpdateDeckRequest = z
  .object({
    name: z.string().trim().min(1).max(100),
    format: z.string().trim().max(30),
    commanders: z.array(z.uuid()).max(2),
    notes: z.string().max(5000),
    /** true makes (or keeps) a share link; false removes it. */
    shared: z.boolean(),
    gallerySection: z.string().trim().max(100),
    galleryOrder: z.number().int().min(0).max(9999).nullable(),
    galleryLabel: z.string().trim().max(60),
  })
  .partial();

export const ScryfallSearchResponse = z.object({
  cards: z.array(Card),
  total: z.number(),
  hasMore: z.boolean(),
});
export type ScryfallSearchResponse = z.infer<typeof ScryfallSearchResponse>;

/** A deck as friends see it: no locations, nothing else from the collection. */
export const PublicDeck = z.object({
  name: z.string(),
  format: z.string(),
  commanders: z.array(z.string()),
  notes: z.string(),
  cards: z.array(z.object({ card: Card, finish: Finish, quantity: z.number() })),
});
export type PublicDeck = z.infer<typeof PublicDeck>;

/** Whether every deck is shared at /decks. */
export const GalleryShare = z.object({ shared: z.boolean() });
export type GalleryShare = z.infer<typeof GalleryShare>;

/** true shares every deck at /decks; false stops it, closing every deck page reached through it. */
export const UpdateGalleryRequest = z.object({ shared: z.boolean() });

/**
 * Every deck, for anyone at /decks while it's shared, in sections: named
 * sections first, then the rest (title null). Each deck opens at /decks/<id>.
 */
export const PublicGallery = z.object({
  sections: z.array(
    z.object({
      title: z.string().nullable(),
      decks: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          format: z.string(),
          label: z.string(),
          commanders: z.array(Card),
          colorIdentity: z.array(z.enum(['W', 'U', 'B', 'R', 'G'])),
        }),
      ),
    }),
  ),
});
export type PublicGallery = z.infer<typeof PublicGallery>;

export const LogEntry = z.object({
  id: z.string(),
  at: z.string(),
  /** "owner" for changes made in the page, "token" for a write token (Claude). */
  actor: z.enum(['owner', 'token']),
  actorName: z.string(),
  action: z.string(),
  summary: z.string(),
});
export type LogEntry = z.infer<typeof LogEntry>;

export const LogList = z.object({ entries: z.array(LogEntry) });

/** Cards in the drawers that sit in the wrong drawer for the filing rules, and where they go. */
export const RefilePlan = z.object({
  moves: z.array(
    z.object({
      copyId: z.string(),
      name: z.string(),
      setCode: z.string(),
      collectorNumber: z.string(),
      finish: Finish,
      quantity: z.number(),
      from: z.string(),
      to: z.string(),
      divider: z.string(),
    }),
  ),
  /** Cards with no spot under the rules (left where they are). */
  noSpot: z.array(z.object({ copyId: z.string(), name: z.string(), quantity: z.number(), location: z.string(), reason: z.string() })),
  applied: z.boolean(),
});
export type RefilePlan = z.infer<typeof RefilePlan>;
